import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { Layout } from '@/components/Layout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import Login from '@/pages/Login'
import VerifyMagicLink from '@/pages/VerifyMagicLink'
import Dashboard from '@/pages/Dashboard'

// Code-split — Recharts (Analytics) and the admin table (Admins) only
// load when their routes are visited, keeping the initial bundle small.
const Analytics    = lazy(() => import('@/pages/Analytics'))
const Admins       = lazy(() => import('@/pages/Admins'))
const Articles     = lazy(() => import('@/pages/Articles'))
const Tenants      = lazy(() => import('@/pages/Tenants'))
const TenantDetail = lazy(() => import('@/pages/TenantDetail'))
const Settings     = lazy(() => import('@/pages/Settings'))
const Conversations = lazy(() => import('@/pages/Conversations'))

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 text-sm">
      Cargando…
    </div>
  )
}

function PageFallback() {
  return (
    <div className="px-8 py-8 text-slate-400 text-sm">Cargando módulo…</div>
  )
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/login" replace />
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </Layout>
  )
}

function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <FullScreenLoader />
  if (user) return <Navigate to="/" replace />
  return <>{children}</>
}

function SuperAdminRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'SUPER_ADMIN') return <Navigate to="/" replace />
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </Layout>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login"        element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
            <Route path="/auth/verify"  element={<VerifyMagicLink />} />
            <Route path="/"             element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/analytics"    element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/conversations" element={<ProtectedRoute><Conversations /></ProtectedRoute>} />
            <Route path="/tenants"      element={<ProtectedRoute><Tenants /></ProtectedRoute>} />
            <Route path="/tenants/:id"  element={<ProtectedRoute><TenantDetail /></ProtectedRoute>} />
            <Route path="/articles"     element={<ProtectedRoute><Articles /></ProtectedRoute>} />
            <Route path="/admins"       element={<SuperAdminRoute><Admins /></SuperAdminRoute>} />
            <Route path="/settings"     element={<SuperAdminRoute><Settings /></SuperAdminRoute>} />
            <Route path="*"             element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  )
}
